import { isBefore, parseISO } from "date-fns";
import { formatInTimeZone } from "date-fns-tz";

import { middleOfDay } from "@/utils/datetime";

import { PregnancyRecord } from "../types";

import { postpartumWeeksAndDaysAt } from "./postpartum";

/**
 * Normalises an ISO datetime to its UTC calendar day at noon, matching pregnancy
 * selector date comparisons elsewhere in the store.
 *
 * @param iso - ISO date or datetime string
 * @returns Calendar-day `Date` at noon local of the UTC date part
 */
function toUtcCalendarDay(iso: string): Date {
  return middleOfDay(formatInTimeZone(parseISO(iso), "UTC", "yyyy-MM-dd"))!;
}

/**
 * Returns whether an encounter service date falls in a pregnancy record's
 * prenatal window or postpartum tracker window (0–182 days from delivery).
 *
 * Active pregnancies match any encounter on or after `startDateTime`.
 * Retired pregnancies with a delivery anchor match:
 * - prenatal: calendar day strictly before delivery (start floor applied only when
 *   `startDateTime` is present and itself precedes delivery — ignores a bogus
 *   start that lands on/after delivery)
 * - postpartum: delivery day through the tracker window
 *
 * @param record - Pregnancy record to test
 * @param encounterStartISO - Encounter `period.start` ISO string
 * @returns `true` when the encounter belongs to this pregnancy's clinical window
 */
export function pregnancyRecordContainsEncounterDate(
  record: PregnancyRecord,
  encounterStartISO: string,
): boolean {
  if (!encounterStartISO) return false;

  const encounterDay = toUtcCalendarDay(encounterStartISO);

  if (record.isActive) {
    if (!record.startDateTime) return false;
    return !isBefore(encounterDay, toUtcCalendarDay(record.startDateTime));
  }

  const deliveryAnchor = record.deliveryDate ?? record.endDateTime;
  if (!deliveryAnchor) {
    if (!record.startDateTime) return false;
    return !isBefore(encounterDay, toUtcCalendarDay(record.startDateTime));
  }

  const deliveryDay = toUtcCalendarDay(deliveryAnchor);

  if (!isBefore(encounterDay, deliveryDay)) {
    return postpartumWeeksAndDaysAt(deliveryAnchor, encounterStartISO) !== undefined;
  }

  if (!record.startDateTime) return true;

  const startDay = toUtcCalendarDay(record.startDateTime);
  // Only enforce the start floor when it is a plausible prenatal onset
  // (strictly before delivery).
  if (isBefore(startDay, deliveryDay) && isBefore(encounterDay, startDay)) {
    return false;
  }

  return true;
}

/**
 * Returns the administrative/clinical end anchor used to rank past pregnancies
 * (`endDateTime`, falling back to `deliveryDate`).
 */
function pregnancyRecordEndAnchor(record: PregnancyRecord): string {
  return record.endDateTime ?? record.deliveryDate ?? "";
}

/**
 * Picks the matching past pregnancy with the latest end/delivery anchor.
 */
function selectMostRecentlyEndedPregnancyRecord(
  records: PregnancyRecord[],
): PregnancyRecord | undefined {
  return [...records].sort((recordA, recordB) =>
    pregnancyRecordEndAnchor(recordB).localeCompare(pregnancyRecordEndAnchor(recordA)),
  )[0];
}

/**
 * Selects the pregnancy record that owns an encounter by service date.
 *
 * Prefers an active matching record when present; otherwise the matching past
 * record with the latest `endDateTime` / `deliveryDate`.
 *
 * @param records - Patient pregnancy records (current and/or historical)
 * @param encounterStartISO - Encounter `period.start` ISO string
 * @returns Matching `PregnancyRecord`, or `undefined` when none apply
 */
export function selectPregnancyRecordForEncounterDate(
  records: PregnancyRecord[],
  encounterStartISO: string | undefined,
): PregnancyRecord | undefined {
  if (!encounterStartISO) return undefined;

  const matches = records.filter((record) =>
    pregnancyRecordContainsEncounterDate(record, encounterStartISO),
  );
  if (matches.length === 0) return undefined;

  const activeMatch = matches.find((record) => record.isActive);
  if (activeMatch) return activeMatch;

  return selectMostRecentlyEndedPregnancyRecord(matches);
}
